import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type MarkdownGroupKind = "plain" | "unordered-list" | "ordered-list" | "blockquote" | "table";

const FENCE_RE = /^\s*```/;
const UNORDERED_LIST_RE = /^\s*[-*+]\s+/;
const ORDERED_LIST_RE = /^\s*\d+\.\s+/;
const BLOCKQUOTE_RE = /^\s*>\s?/;
const TABLE_ROW_RE = /^\s*\|.+\|\s*$/;

function detectGroupKind(line: string): MarkdownGroupKind {
  if (UNORDERED_LIST_RE.test(line)) return "unordered-list";
  if (ORDERED_LIST_RE.test(line)) return "ordered-list";
  if (BLOCKQUOTE_RE.test(line)) return "blockquote";
  if (TABLE_ROW_RE.test(line)) return "table";
  return "plain";
}

function buildMarkdownSource(lines: readonly string[]): string {
  const blocks: string[] = [];
  let currentBlock: string[] = [];
  let currentKind: MarkdownGroupKind | null = null;
  let inFence = false;

  const flushBlock = () => {
    if (currentBlock.length === 0) return;
    blocks.push(currentBlock.join("\n"));
    currentBlock = [];
    currentKind = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushBlock();
      inFence = false;
      continue;
    }

    if (FENCE_RE.test(line)) {
      if (inFence) {
        currentBlock.push(line);
        flushBlock();
        inFence = false;
        continue;
      }
      flushBlock();
      currentBlock = [line];
      inFence = true;
      continue;
    }

    if (inFence) {
      currentBlock.push(line);
      continue;
    }

    const kind = detectGroupKind(line);
    if (currentBlock.length > 0 && currentKind === kind && kind !== "plain") {
      currentBlock.push(line);
      continue;
    }

    flushBlock();
    currentBlock = [line];
    currentKind = kind;
  }

  flushBlock();
  return blocks.join("\n\n");
}

export function LessonMarkdown({ lines }: { lines: readonly string[] }) {
  const source = buildMarkdownSource(lines);
  if (!source) return null;

  return (
    <div className="ide-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noreferrer" />,
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
